import type { CountryCode, EntityType, KycDepth, KycStatus } from './client.js';

// KYC document categories. Each row is a checklist item shown on
// /portal/compliance. The required_for_entity_types / required_for_countries
// arrays drive `getRequiredCategories()` below — an empty array means
// "applies to every entity type / country".
//
// Labels + descriptions are DRAFT until UK compliance solicitor sign-off
// (REQ-013, PLAN §12a). They're present now so the types compile and the
// shared rollup logic can reference category keys; final wording lands in
// Phase F with the solicitor review.

export interface KycCategoryConfig {
  readonly key: string;
  readonly label: string;
  readonly required_for_entity_types: ReadonlyArray<EntityType>;
  readonly required_for_countries: ReadonlyArray<CountryCode>;
  readonly description: string;
}

// Per-entity-type rules (PLAN §3a seed list):
//   - certificate_of_incorporation  : all except FZ-LLC
//   - memorandum_articles            : Ltd / PLC / LLP only
//   - register_of_directors          : all
//   - register_of_members            : Ltd / PLC / LLP only
//   - trade_licence                  : FZ-LLC only (UAE free zone)
//   - proof_of_address               : all
//   - ubo_*                          : all
//   - source_of_funds_declaration    : all
//   - bank_statements_6m             : all
//   - id_document                    : all
//   - proof_of_address_3m            : all
export const KYC_CATEGORIES: ReadonlyArray<KycCategoryConfig> = [
  {
    key: 'corporate.certificate_of_incorporation',
    label: 'Certificate of incorporation',
    required_for_entity_types: ['Ltd', 'PLC', 'LLP', 'Other'],
    required_for_countries: [],
    description: 'DRAFT — Companies House / equivalent registry certificate showing the legal formation of the entity.',
  },
  {
    key: 'corporate.memorandum_articles',
    label: 'Memorandum and articles of association',
    required_for_entity_types: ['Ltd', 'PLC', 'LLP'],
    required_for_countries: [],
    description: 'DRAFT — current memorandum and articles of association (or equivalent constitutional documents).',
  },
  {
    key: 'corporate.register_of_directors',
    label: 'Register of directors',
    required_for_entity_types: [],
    required_for_countries: [],
    description: 'DRAFT — current list of directors / officers with appointment and resignation dates.',
  },
  {
    key: 'corporate.register_of_members',
    label: 'Register of members / shareholders',
    required_for_entity_types: ['Ltd', 'PLC', 'LLP'],
    required_for_countries: [],
    description: 'DRAFT — current shareholder register including holdings and any beneficial-ownership flags.',
  },
  {
    key: 'corporate.trade_licence',
    label: 'Trade licence',
    required_for_entity_types: ['FZ-LLC'],
    required_for_countries: [],
    description: 'DRAFT — current free-zone trade licence (UAE Free-Zone LLCs only).',
  },
  {
    key: 'corporate.proof_of_address',
    label: 'Proof of registered office address',
    required_for_entity_types: [],
    required_for_countries: [],
    description: 'DRAFT — recent utility bill or government document evidencing the registered office address.',
  },
  {
    key: 'beneficial_ownership.ubo_declaration',
    label: 'Ultimate beneficial-owner declaration',
    required_for_entity_types: [],
    required_for_countries: [],
    description: 'DRAFT — signed declaration of UBOs (≥25% holding) including names, DOB, nationality, and residency.',
  },
  {
    key: 'beneficial_ownership.ubo_passport',
    label: 'UBO photo identification',
    required_for_entity_types: [],
    required_for_countries: [],
    description: 'DRAFT — government-issued photo ID for each UBO (passport preferred).',
  },
  {
    key: 'beneficial_ownership.ubo_proof_of_address',
    label: 'UBO proof of address',
    required_for_entity_types: [],
    required_for_countries: [],
    description: 'DRAFT — recent utility bill or bank statement (within 3 months) for each UBO.',
  },
  {
    key: 'source_of_funds.source_of_funds_declaration',
    label: 'Source of funds declaration',
    required_for_entity_types: [],
    required_for_countries: [],
    description: 'DRAFT — written declaration of the origin of funds being introduced for the engagement.',
  },
  {
    key: 'source_of_funds.bank_statements_6m',
    label: 'Six months of bank statements',
    required_for_entity_types: [],
    required_for_countries: [],
    description: 'DRAFT — six months of statements from the entity\'s primary operating account.',
  },
  {
    key: 'contact_identity.id_document',
    label: 'Authorised contact photo identification',
    required_for_entity_types: [],
    required_for_countries: [],
    description: 'DRAFT — government-issued photo ID for the portal contact representing the entity.',
  },
  {
    key: 'contact_identity.proof_of_address_3m',
    label: 'Authorised contact proof of address',
    required_for_entity_types: [],
    required_for_countries: [],
    description: 'DRAFT — recent utility bill or bank statement (within 3 months) for the portal contact.',
  },
];

// Simplified-due-diligence minimum (MLR 2017 risk-based CDD). When a client's
// kyc_depth is 'simplified', getRequiredCategories() returns ONLY these keys
// instead of the full entity/country-driven set. Intended for lower-risk
// parties such as commission-based introducers / agents who consume our
// services but cannot reasonably provide the full corporate + UBO +
// source-of-funds set. DRAFT scope — the exact minimum is pending UK
// compliance solicitor sign-off (REQ-013). Note: SDD still requires
// *identifying* the party; it never removes CDD entirely.
export const SIMPLIFIED_REQUIRED_KEYS: ReadonlyArray<string> = [
  'contact_identity.id_document',
];

/**
 * Returns the category keys required for a given client. Pure function.
 *
 * - `kyc_depth === 'simplified'` → only SIMPLIFIED_REQUIRED_KEYS (reduced set
 *   for lower-risk parties; the rest become optional / not-applicable).
 * - otherwise (standard / enhanced / unset) → a category applies if its
 *   `required_for_entity_types` is empty OR includes the client's entity_type
 *   AND its `required_for_countries` is empty OR includes the client's country.
 */
export function getRequiredCategories(client: {
  entity_type: EntityType;
  country: CountryCode;
  kyc_depth?: KycDepth;
}): string[] {
  if (client.kyc_depth === 'simplified') {
    return KYC_CATEGORIES
      .filter((cat) => SIMPLIFIED_REQUIRED_KEYS.includes(cat.key))
      .map((cat) => cat.key);
  }

  const out: string[] = [];
  for (const cat of KYC_CATEGORIES) {
    const entityOk = cat.required_for_entity_types.length === 0
      || cat.required_for_entity_types.includes(client.entity_type);
    const countryOk = cat.required_for_countries.length === 0
      || cat.required_for_countries.includes(client.country);
    if (entityOk && countryOk) out.push(cat.key);
  }
  return out;
}

/**
 * Human-readable label for the client.kyc rollup. Two perspectives:
 * staff sees operational language; client sees softer "what does this mean
 * for me" framing.
 */
export function getKycStatusLabel(
  status: KycStatus,
  perspective: 'staff' | 'client',
): string {
  if (perspective === 'staff') {
    switch (status) {
      case 'pending':   return 'Pending';
      case 'in_review': return 'In review';
      case 'current':   return 'Current';
      case 'refresh':   return 'Refresh due';
      case 'expired':   return 'Expired';
      case 'rejected':  return 'Rejected';
    }
  }
  switch (status) {
    case 'pending':   return 'Documents needed';
    case 'in_review': return 'Under review';
    case 'current':   return 'Verified';
    case 'refresh':   return 'Refresh due';
    case 'expired':   return 'Renewal required';
    case 'rejected':  return 'Action needed';
  }
}
